import { useCallback, useEffect, useRef } from "react";

// Largura da zona de borda (px) que dispara o scroll, e velocidade maxima
// (px por frame) quando o cursor cola na borda.
const EDGE_PX = 90;
const MAX_SPEED_PX = 24;

/**
 * Auto-scroll horizontal do board durante drag HTML5 nativo (Fase B).
 *
 * O drag-and-drop nativo NAO rola o container quando o card chega na borda
 * lateral — o usuario fica preso. Este hook escuta o `dragover` no container
 * do kanban: quando o cursor entra na zona de borda (esq/dir), roda um loop
 * `requestAnimationFrame` que rola o container proporcionalmente a proximidade
 * da borda (mais perto = mais rapido). Para quando o cursor sai da borda ou o
 * drag termina (`ativo=false`).
 *
 * Uso:
 *   const ref = useRef(null);
 *   const onDragOver = useKanbanAutoScroll(ref, Boolean(dragLeadId));
 *   <div className="kanban" ref={ref} onDragOver={onDragOver}>...</div>
 */
export default function useKanbanAutoScroll(ref, ativo) {
  const rafRef = useRef(null);
  const dirRef = useRef(0); // -1 esquerda, 1 direita, 0 parado
  const speedRef = useRef(0);

  const stop = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    dirRef.current = 0;
    speedRef.current = 0;
  }, []);

  const tick = useCallback(() => {
    const el = ref.current;
    if (el && dirRef.current !== 0 && speedRef.current > 0) {
      el.scrollLeft += dirRef.current * speedRef.current;
      rafRef.current = requestAnimationFrame(tick);
    } else {
      rafRef.current = null;
    }
  }, [ref]);

  const onDragOver = useCallback(
    (e) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX;
      let dir = 0;
      let speed = 0;
      // x === 0 acontece no dragend/drop espurio do DnD nativo — ignora pra
      // nao rolar pra esquerda sem querer.
      if (x > 0) {
        if (x < rect.left + EDGE_PX) {
          dir = -1;
          speed = Math.ceil(((rect.left + EDGE_PX - x) / EDGE_PX) * MAX_SPEED_PX);
        } else if (x > rect.right - EDGE_PX) {
          dir = 1;
          speed = Math.ceil(((x - (rect.right - EDGE_PX)) / EDGE_PX) * MAX_SPEED_PX);
        }
      }
      dirRef.current = dir;
      speedRef.current = speed;
      if (dir !== 0 && rafRef.current == null) {
        rafRef.current = requestAnimationFrame(tick);
      } else if (dir === 0) {
        stop();
      }
    },
    [ref, tick, stop]
  );

  // Para o loop ao fim do drag (ou no unmount).
  useEffect(() => {
    if (!ativo) stop();
    return stop;
  }, [ativo, stop]);

  return onDragOver;
}
